import type { ThemeTokens } from '@solote/contracts';

/**
 * APLICAÇÃO DO TEMA
 *
 * O tema chega do painel em tempo de execução, então não pode ser resolvido
 * em build. O Tailwind v4 torna isso possível: cada token do `@theme` vira
 * uma custom property real, e toda utilitária (`bg-primary`, `text-primary`)
 * compila para `var(--color-primary)`. Sobrescrever a variável repinta o
 * portal inteiro sem rebuild.
 *
 * O DETALHE QUE IMPORTA
 *
 * A primeira versão gravava as variáveis inline no `<html>`. Parece
 * equivalente, mas não é: declaração inline vence qualquer regra de
 * stylesheet, inclusive `@media (prefers-color-scheme: dark)`. O efeito era
 * o tema publicado apagar o modo escuro — fundo escuro com cores de tema
 * claro, texto sem contraste.
 *
 * Por isso a aplicação é feita por um `<style>` injetado, que participa da
 * cascata normalmente e respeita os três estados do visualizador: sistema,
 * claro explícito e escuro explícito.
 */

const STYLE_ELEMENT_ID = 'solote-runtime-theme';

const TOKEN_GROUPS = ['colors', 'fonts', 'radii', 'spacing', 'shadows'] as const;

/** `colors.primary` -> `--color-primary`. */
function cssVariableName(group: string, key: string): string {
  const prefix =
    group === 'colors' ? 'color' : group === 'fonts' ? 'font' : group === 'radii' ? 'radius' : group === 'shadows' ? 'shadow' : 'space';
  return `--${prefix}-${key.replace(/[^a-zA-Z0-9-]/g, '-')}`;
}

/**
 * Valores de tema são dados vindos do banco. Um `}` solto fecharia a regra e
 * o que viesse depois viraria CSS arbitrário — por isso a validação é
 * restritiva por padrão, e o que não passa é descartado em silêncio.
 */
const SAFE_VALUE = /^[#a-zA-Z0-9 ,.()%_'"/+-]{1,200}$/;

function declarationsFor(entries: Record<string, string> | undefined, group: string): string[] {
  if (!entries) return [];

  const declarations: string[] = [];
  for (const [key, value] of Object.entries(entries)) {
    if (typeof value !== 'string' || !SAFE_VALUE.test(value)) continue;
    declarations.push(`${cssVariableName(group, key)}:${value}`);
  }

  return declarations;
}

/** Monta a folha completa com os três estados de tema. */
export function buildThemeStylesheet(theme: ThemeTokens): string {
  const base = TOKEN_GROUPS.flatMap((group) => declarationsFor(theme[group], group));
  const dark = declarationsFor(theme.darkColors, 'colors');

  if (base.length === 0 && dark.length === 0) return '';

  const blocks: string[] = [];

  if (base.length > 0) {
    blocks.push(`:root{${base.join(';')}}`);
  }

  if (dark.length > 0) {
    const joined = dark.join(';');
    // Sistema em escuro, sem escolha explícita de claro.
    blocks.push(`@media (prefers-color-scheme:dark){:root:not([data-theme="light"]){${joined}}}`);
    // Escolha explícita de escuro vence o sistema em claro.
    blocks.push(`:root[data-theme="dark"]{${joined}}`);
  }

  return blocks.join('');
}

/**
 * Carimba a aparencia escolhida no `<html>`.
 *
 * `light` e `dark` sao explicitos e vencem o sistema operacional nos dois
 * sentidos — e para isso que os seletores `[data-theme="…"]` existem na folha.
 * `system` remove o carimbo e devolve a decisao ao `prefers-color-scheme`.
 */
function applyAppearance(appearance: ThemeTokens['appearance'], target: Document): void {
  const root = target.documentElement;

  if (appearance === 'system') {
    root.removeAttribute('data-theme');
    return;
  }

  if (root.getAttribute('data-theme') !== appearance) {
    root.setAttribute('data-theme', appearance);
  }
}

/** Injeta (ou atualiza) a folha de tema. Idempotente. */
export function applyTheme(theme: ThemeTokens, target: Document = document): void {
  applyAppearance(theme.appearance, target);

  const css = buildThemeStylesheet(theme);

  let element = target.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;

  if (!css) {
    element?.remove();
    return;
  }

  if (!element) {
    element = target.createElement('style');
    element.id = STYLE_ELEMENT_ID;
    // No fim do <head>: precisa vir depois da folha do Tailwind para
    // sobrescrever os valores de partida do `@theme`.
    target.head.appendChild(element);
  }

  if (element.textContent !== css) {
    element.textContent = css;
  }
}

/** Usado no SSR/pré-render para evitar flash do tema padrão. */
export function themeToInlineStyle(theme: ThemeTokens): string {
  return buildThemeStylesheet(theme);
}
